/**
 * Solid-facing wrapper around the simulation worker.
 *
 * Owns one worker at a time. Cancelling terminates it — the simulation loop is
 * synchronous, so a cancel *message* would sit unread until the run finished.
 * Terminating is immediate and cannot leave a stale run posting results at a
 * screen that has moved on. A fresh worker is spun up on the next run.
 */

import { createSignal, onCleanup } from 'solid-js';
import type { SimulationInput, SimulationResult } from '~/domain/simulate';
import type { CampaignBatchInput, CampaignBatchResult } from '~/domain/session';
import type {
  SimulationRequest,
  SimulationResponse,
  StrategyRunRequest,
} from '~/worker/simulation.worker';
import type { StrategyRun } from '~/domain/simulation';

export interface WorkerRun<TInput, TResult> {
  running: () => boolean;
  progress: () => number;
  result: () => TResult | null;
  error: () => string | null;
  run: (input: TInput) => void;
  cancel: () => void;
  clear: () => void;
}

export type SimulationRun = WorkerRun<SimulationInput, SimulationResult>;
export type CampaignRun = WorkerRun<CampaignBatchInput, CampaignBatchResult>;
export type StrategiesRun = WorkerRun<StrategyRunRequest, StrategyRun[]>;

/**
 * Strip reactivity before crossing the worker boundary.
 *
 * `editor.bets()` is a `solid-js/store` array, which is a Proxy — and
 * structured clone refuses Proxies with a DataCloneError. The store serialises
 * cleanly through JSON (it is plain data all the way down: no Dates, Maps or
 * class instances), so a round-trip is both correct and cheap relative to the
 * run that follows.
 */
function toPlain<T>(input: T): T {
  return JSON.parse(JSON.stringify(input)) as T;
}

/**
 * The shared plumbing. Both run kinds use one worker script, one request id
 * sequence and one cancellation rule; only the message they send and the reply
 * they accept differ.
 */
function createWorkerRunner<TInput, TResult>(
  buildRequest: (id: number, input: TInput) => SimulationRequest,
  doneKind: SimulationResponse['kind'],
): WorkerRun<TInput, TResult> {
  const [running, setRunning] = createSignal(false);
  const [progress, setProgress] = createSignal(0);
  const [result, setResult] = createSignal<TResult | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  let worker: Worker | null = null;
  let requestId = 0;

  const dispose = () => {
    worker?.terminate();
    worker = null;
  };
  onCleanup(dispose);

  const start = (input: TInput) => {
    dispose();
    requestId += 1;
    const id = requestId;

    setRunning(true);
    setProgress(0);
    setError(null);

    try {
      // Vite resolves worker URLs statically, so this must be a relative
      // literal — the `~/` alias is not visible to that transform.
      worker = new Worker(new URL('../worker/simulation.worker.ts', import.meta.url), {
        type: 'module',
      });
    } catch (cause) {
      setRunning(false);
      setError(cause instanceof Error ? cause.message : 'Could not start the simulation worker');
      return;
    }

    worker.onmessage = (event: MessageEvent<SimulationResponse>) => {
      const message = event.data;
      // Ignore anything from a superseded run.
      if (message.id !== id) return;

      if (message.kind === 'progress') {
        setProgress(message.total === 0 ? 0 : message.completed / message.total);
        return;
      }
      if (message.kind === 'error') {
        setError(message.message);
        setRunning(false);
        dispose();
        return;
      }
      if (message.kind === doneKind) {
        setResult(() => message.result as TResult);
        setProgress(1);
        setRunning(false);
        dispose();
      }
    };

    worker.onerror = (event) => {
      setError(event.message || 'The simulation worker failed');
      setRunning(false);
      dispose();
    };

    worker.postMessage(buildRequest(id, toPlain(input)));
  };

  return {
    running,
    progress,
    result,
    error,
    run: start,
    cancel: () => {
      dispose();
      setRunning(false);
      setProgress(0);
    },
    clear: () => {
      setResult(null);
      setError(null);
      setProgress(0);
    },
  };
}

export function createSimulationRunner(): SimulationRun {
  return createWorkerRunner<SimulationInput, SimulationResult>(
    (id, input) => ({ kind: 'run', id, input }),
    'done',
  );
}

export function createCampaignRunner(): CampaignRun {
  return createWorkerRunner<CampaignBatchInput, CampaignBatchResult>(
    (id, input) => ({ kind: 'campaign', id, input }),
    'campaign-done',
  );
}

/**
 * A multi-strategy run (SPEC-V2.md §5). One request carries every entry, so
 * they share a seed by construction rather than by the caller remembering to
 * pass the same one twice.
 */
export function createStrategiesRunner(): StrategiesRun {
  return createWorkerRunner<StrategyRunRequest, StrategyRun[]>(
    (id, input) => ({ kind: 'strategies', id, input }),
    'strategies-done',
  );
}
